import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  ArticleTypographySettings, 
  DEFAULT_TYPOGRAPHY, 
  HeadingSettings, 
  BodySettings,
  QuoteFormatSettings
} from '@/types/articleTypography';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';

export function useArticleTypography(brandId: string | null) {
  const queryClient = useQueryClient();

  const { data: typography, isLoading, error } = useQuery({
    queryKey: ['article-typography', brandId],
    queryFn: async () => {
      if (!brandId) return null;

      const { data, error } = await supabase
        .from('os_articles_typography_settings')
        .select('*')
        .eq('brand_id', brandId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        return {
          ...DEFAULT_TYPOGRAPHY,
          brand_id: brandId
        };
      }

      return {
        id: data.id,
        brand_id: data.brand_id,
        h1_settings: data.h1_settings as unknown as HeadingSettings,
        h2_settings: data.h2_settings as unknown as HeadingSettings,
        h3_settings: data.h3_settings as unknown as HeadingSettings,
        body_settings: data.body_settings as unknown as BodySettings,
        created_at: data.created_at,
        updated_at: data.updated_at
      } as ArticleTypographySettings;
    },
    enabled: !!brandId
  });

  const saveMutation = useMutation({
    mutationFn: async (settings: ArticleTypographySettings) => {
      const { data: existing } = await supabase
        .from('os_articles_typography_settings')
        .select('id')
        .eq('brand_id', settings.brand_id)
        .maybeSingle();

      const payload = {
        h1_settings: settings.h1_settings as unknown as Json,
        h2_settings: settings.h2_settings as unknown as Json,
        h3_settings: settings.h3_settings as unknown as Json,
        body_settings: settings.body_settings as unknown as Json
      };

      if (existing) {
        const { error } = await supabase
          .from('os_articles_typography_settings')
          .update(payload)
          .eq('brand_id', settings.brand_id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('os_articles_typography_settings')
          .insert({
            brand_id: settings.brand_id,
            ...payload
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-typography', brandId] });
      toast.success('Typography settings saved');
    },
    onError: (error) => {
      console.error('Error saving typography:', error);
      toast.error('Failed to save typography settings');
    }
  });

  return {
    typography,
    isLoading,
    error,
    saveTypography: saveMutation.mutate,
    isSaving: saveMutation.isPending
  };
}

// Database row interface for quote formats
interface QuoteFormatRow {
  id: string;
  brand_id: string;
  name: string;
  quote_icon_color: string;
  quote_icon_size: string;
  quote_font_size: string;
  quote_font_weight: string;
  quote_font_family: string;
  quote_font_style: string;
  quote_line_height: string;
  quote_color: string;
  attribution_font_size: string;
  attribution_font_weight: string;
  attribution_color: string;
  attribution_font_style: string;
  background_color: string;
  padding_vertical: string;
  padding_horizontal: string;
  margin_vertical: string;
  sort_order: number;
}

// Convert database row to QuoteFormatSettings
function rowToQuoteFormat(row: QuoteFormatRow): QuoteFormatSettings {
  return {
    id: row.id,
    name: row.name,
    quoteIconColor: row.quote_icon_color,
    quoteIconSize: row.quote_icon_size,
    quoteFontSize: row.quote_font_size,
    quoteFontWeight: row.quote_font_weight,
    quoteFontFamily: row.quote_font_family,
    quoteFontStyle: row.quote_font_style,
    quoteLineHeight: row.quote_line_height,
    quoteColor: row.quote_color,
    attributionFontSize: row.attribution_font_size,
    attributionFontWeight: row.attribution_font_weight,
    attributionColor: row.attribution_color,
    attributionFontStyle: row.attribution_font_style,
    backgroundColor: row.background_color,
    paddingVertical: row.padding_vertical,
    paddingHorizontal: row.padding_horizontal,
    marginVertical: row.margin_vertical,
  };
}

// Convert QuoteFormatSettings to database row format
function quoteFormatToRow(format: QuoteFormatSettings, brandId: string, sortOrder: number): Omit<QuoteFormatRow, 'id'> & { id?: string } {
  return {
    id: format.id.startsWith('format-') ? undefined : format.id, // Only include id if it's a real UUID
    brand_id: brandId,
    name: format.name,
    quote_icon_color: format.quoteIconColor,
    quote_icon_size: format.quoteIconSize,
    quote_font_size: format.quoteFontSize,
    quote_font_weight: format.quoteFontWeight,
    quote_font_family: format.quoteFontFamily,
    quote_font_style: format.quoteFontStyle,
    quote_line_height: format.quoteLineHeight,
    quote_color: format.quoteColor,
    attribution_font_size: format.attributionFontSize,
    attribution_font_weight: format.attributionFontWeight,
    attribution_color: format.attributionColor,
    attribution_font_style: format.attributionFontStyle,
    background_color: format.backgroundColor,
    padding_vertical: format.paddingVertical,
    padding_horizontal: format.paddingHorizontal,
    margin_vertical: format.marginVertical,
    sort_order: sortOrder,
  };
}

// Hook for quote formats
export function useArticleQuoteFormats(brandId: string | null) {
  const queryClient = useQueryClient();

  const { data: quoteFormats, isLoading } = useQuery({
    queryKey: ['article-quote-formats', brandId],
    queryFn: async () => {
      if (!brandId) return [];

      const { data, error } = await supabase
        .from('os_articles_quote_formats')
        .select('*')
        .eq('brand_id', brandId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return (data || []).map(rowToQuoteFormat);
    },
    enabled: !!brandId
  });

  const saveMutation = useMutation({
    mutationFn: async (formats: QuoteFormatSettings[]) => {
      if (!brandId) throw new Error('No brand selected');

      // Delete existing formats for this brand
      await supabase
        .from('os_articles_quote_formats')
        .delete()
        .eq('brand_id', brandId);

      // Insert new formats
      if (formats.length > 0) {
        const rows = formats.map((format, index) => {
          const row = quoteFormatToRow(format, brandId, index);
          // Remove id if it's undefined (for new records)
          if (!row.id) {
            const { id, ...rest } = row;
            return rest;
          }
          return row;
        });

        const { error } = await supabase
          .from('os_articles_quote_formats')
          .insert(rows);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-quote-formats', brandId] });
      toast.success('Quote formats saved');
    },
    onError: (error) => {
      console.error('Error saving quote formats:', error);
      toast.error('Failed to save quote formats');
    }
  });

  return {
    quoteFormats: quoteFormats || [],
    isLoading,
    saveQuoteFormats: saveMutation.mutate,
    isSaving: saveMutation.isPending
  };
}

// Hook for public/API access using RPC
export function usePublicArticleTypography(brandId: string | null) {
  return useQuery({
    queryKey: ['public-article-typography', brandId],
    queryFn: async () => {
      if (!brandId) return null;

      const { data, error } = await supabase.rpc('get_article_typography_settings', {
        p_brand_id: brandId
      });

      if (error) throw error;
      return data;
    },
    enabled: !!brandId
  });
}

// Generate CSS for a single quote format
export function generateQuoteFormatCSS(format: QuoteFormatSettings, selector: string, index: number): string {
  const className = index === 0 ? 'blockquote, .quote-format' : `.quote-format-${index + 1}`;
  
  return `
${selector} ${className} {
  position: relative;
  background-color: ${format.backgroundColor};
  padding: ${format.paddingVertical} ${format.paddingHorizontal};
  margin: ${format.marginVertical} 0;
}

${selector} ${className}::before {
  content: '"';
  position: absolute;
  top: -0.5rem;
  left: -0.5rem;
  font-family: Georgia, serif;
  font-size: ${format.quoteIconSize};
  color: ${format.quoteIconColor};
  line-height: 1;
}

${selector} ${className} p,
${selector} ${className} .quote-text {
  position: relative;
  z-index: 1;
  padding-left: 2rem;
  font-size: ${format.quoteFontSize};
  font-weight: ${format.quoteFontWeight};
  font-family: ${format.quoteFontFamily};
  font-style: ${format.quoteFontStyle};
  line-height: ${format.quoteLineHeight};
  color: ${format.quoteColor};
  margin: 0;
}

${selector} ${className} cite,
${selector} ${className} .quote-attribution {
  display: block;
  margin-top: 1rem;
  padding-left: 2rem;
  font-size: ${format.attributionFontSize};
  font-weight: ${format.attributionFontWeight};
  font-style: ${format.attributionFontStyle};
  color: ${format.attributionColor};
}

${selector} ${className} cite::before,
${selector} ${className} .quote-attribution::before {
  content: '— ';
}`;
}

// Generate CSS from typography settings
export function generateTypographyCSS(
  typography: ArticleTypographySettings | null, 
  selector: string = '.medium-editor .ProseMirror',
  quoteFormats?: QuoteFormatSettings[]
): string {
  if (!typography) return '';

  const h1 = typography.h1_settings;
  const h2 = typography.h2_settings;
  const h3 = typography.h3_settings;
  const body = typography.body_settings;

  let css = `
${selector} h1 {
  font-size: ${h1.fontSize};
  font-weight: ${h1.fontWeight};
  font-family: ${h1.fontFamily};
  font-style: ${h1.fontStyle};
  line-height: ${h1.lineHeight};
  margin-top: ${h1.marginTop};
  margin-bottom: ${h1.marginBottom};
}

${selector} h2 {
  font-size: ${h2.fontSize};
  font-weight: ${h2.fontWeight};
  font-family: ${h2.fontFamily};
  font-style: ${h2.fontStyle};
  line-height: ${h2.lineHeight};
  margin-top: ${h2.marginTop};
  margin-bottom: ${h2.marginBottom};
}

${selector} h3 {
  font-size: ${h3.fontSize};
  font-weight: ${h3.fontWeight};
  font-family: ${h3.fontFamily};
  font-style: ${h3.fontStyle};
  line-height: ${h3.lineHeight};
  margin-top: ${h3.marginTop};
  margin-bottom: ${h3.marginBottom};
}

${selector} p {
  font-size: ${body.fontSize};
  line-height: ${body.lineHeight};
  margin-bottom: ${body.marginBottom};
}`;

  // Add quote format CSS if provided
  if (quoteFormats && quoteFormats.length > 0) {
    quoteFormats.forEach((format, index) => {
      css += generateQuoteFormatCSS(format, selector, index);
    });
  }

  return css;
}
